import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { format, parseISO } from 'date-fns';

const BookingSummary = ({ bookingData }) => {
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'MMMM dd, yyyy');
    } catch {
      return 'Date not available';
    }
  };

  const formatTime = (dateString) => {
    try {
      return format(parseISO(dateString), 'h:mm a');
    } catch {
      return '';
    }
  };

  const summaryDetails = [
    {
      label: 'Booking ID',
      value: bookingData?.id?.substring(0, 8)?.toUpperCase() || 'N/A',
      icon: 'Hash'
    },
    {
      label: 'Client Name',
      value: bookingData?.clientName || 'N/A',
      icon: 'User'
    },
    {
      label: 'Email',
      value: bookingData?.clientEmail || 'N/A',
      icon: 'Mail'
    },
    {
      label: 'Phone',
      value: bookingData?.clientPhone || 'N/A',
      icon: 'Phone'
    },
    {
      label: 'Company',
      value: bookingData?.company || 'N/A',
      icon: 'Building'
    },
    {
      label: 'Service Type',
      value: bookingData?.serviceInterest || 'General Consultation',
      icon: 'Briefcase'
    },
    {
      label: 'Date',
      value: formatDate(bookingData?.scheduledStart),
      icon: 'Calendar'
    },
    {
      label: 'Time',
      value: `${formatTime(bookingData?.scheduledStart)} - ${formatTime(bookingData?.scheduledEnd)}`,
      icon: 'Clock'
    }
  ];

  const contactOptions = [
    {
      title: 'Email Support',
      description: 'Get help via email within 24 hours',
      icon: 'Mail',
      action: 'support@northstaranalytics.eu',
      color: 'bg-[#002B45]'
    },
    {
      title: 'Live Chat',
      description: 'Instant assistance during business hours',
      icon: 'MessageCircle',
      action: 'Start Chat',
      color: 'bg-purple-500'
    }
  ];

  return (
    <section className="py-16 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Booking Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-premium p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-[#002B45]">
                  Booking Summary
                </h2>
                <div className="flex items-center space-x-2 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full">
                  <Icon name="CheckCircle" size={16} />
                  <span className="text-sm font-semibold">Confirmed</span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {summaryDetails?.map((detail, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-[#002B45]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={detail?.icon} size={18} className="text-[#002B45]" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                        {detail?.label}
                      </p>
                      <p className="text-sm font-medium text-[#002B45]">{detail?.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  iconName="Calendar"
                  onClick={() => setShowRescheduleModal(true)}
                  className="flex-1 min-w-[200px]"
                >
                  Reschedule Consultation
                </Button>
                <Button
                  variant="outline"
                  iconName="XCircle"
                  className="flex-1 min-w-[200px] text-red-600 border-red-600 hover:bg-red-50"
                >
                  Cancel Booking
                </Button>
              </div>

              {/* Important Notes */}
              <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertCircle" size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold mb-1">Rescheduling & Cancellation Policy</p>
                    <ul className="list-disc list-inside space-y-1 text-amber-700">
                      <li>Reschedule up to 24 hours before your appointment at no charge</li>
                      <li>Cancellations within 24 hours may incur a fee</li>
                      <li>No-shows will be charged the full consultation fee</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact & Support */}
          <div className="space-y-6">
            {/* Need Help Card */}
            <div className="bg-gradient-to-br from-[#002B45] to-[#003D5C] rounded-2xl shadow-premium p-8 text-white">
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mb-4">
                <Icon name="Headphones" size={28} className="text-[#1FB5A5]" />
              </div>
              <h3 className="text-xl font-bold mb-2">Need Assistance?</h3>
              <p className="text-white/80 text-sm mb-6">
                Our support team is here to help with any questions or changes to your booking.
              </p>

              <div className="space-y-3">
                {contactOptions?.map((option, index) => (
                  <button
                    key={index}
                    className="w-full bg-white/10 hover:bg-white/20 rounded-lg p-3 transition-all duration-300 text-left group"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`${option?.color} w-10 h-10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <Icon name={option?.icon} size={18} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{option?.title}</p>
                        <p className="text-xs text-white/70">{option?.description}</p>
                      </div>
                      <Icon name="ChevronRight" size={16} className="text-white/50" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-2xl shadow-premium p-6">
              <h3 className="text-lg font-bold text-[#002B45] mb-4">Quick Links</h3>
              <div className="space-y-2">
                <a
                  href="/frequently-asked-questions-comprehensive-faq-center"
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <div className="flex items-center space-x-3">
                    <Icon name="HelpCircle" size={18} className="text-[#1FB5A5]" />
                    <span className="text-sm font-medium text-[#002B45]">FAQ</span>
                  </div>
                  <Icon name="ChevronRight" size={16} className="text-gray-400 group-hover:text-[#1FB5A5]" />
                </a>
                <a
                  href="/client-portal-dashboard-personalized-analytics-hub"
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <div className="flex items-center space-x-3">
                    <Icon name="LayoutDashboard" size={18} className="text-[#1FB5A5]" />
                    <span className="text-sm font-medium text-[#002B45]">Client Portal</span>
                  </div>
                  <Icon name="ChevronRight" size={16} className="text-gray-400 group-hover:text-[#1FB5A5]" />
                </a>
                <a
                  href="/knowledge-hub-thought-leadership-center"
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <div className="flex items-center space-x-3">
                    <Icon name="BookOpen" size={18} className="text-[#1FB5A5]" />
                    <span className="text-sm font-medium text-[#002B45]">Knowledge Hub</span>
                  </div>
                  <Icon name="ChevronRight" size={16} className="text-gray-400 group-hover:text-[#1FB5A5]" />
                </a>
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="bg-white rounded-2xl shadow-premium p-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Icon name="Shield" size={20} className="text-[#1FB5A5]" />
                  <span className="text-sm text-gray-700">GDPR Compliant</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="Lock" size={20} className="text-[#1FB5A5]" />
                  <span className="text-sm text-gray-700">Secure Data Handling</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonial Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-premium p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-[#002B45] mb-2">
              What Our Clients Say
            </h3>
            <p className="text-gray-600">Hear from organizations we've helped transform</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote: "The consultation was incredibly valuable. The team understood our challenges immediately and provided actionable insights.",
                author: "Sarah Mitchell",
                role: "CTO, TechCorp",
                rating: 5
              },
              {
                quote: "Northstar's data strategy expertise helped us unlock significant value from our data assets. Highly recommended!",
                author: "James O'Brien",
                role: "Head of Analytics, FinServe",
                rating: 5
              },
              {
                quote: "Professional, knowledgeable, and results-driven. The consultation exceeded our expectations in every way.",
                author: "Emma Thompson",
                role: "VP Operations, RetailCo",
                rating: 5
              }
            ]?.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center space-x-1 mb-3">
                  {[...Array(testimonial?.rating)]?.map((_, i) => (
                    <Icon key={i} name="Star" size={16} className="text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-sm text-gray-700 mb-4 italic">"{testimonial?.quote}"</p>
                <div>
                  <p className="font-semibold text-[#002B45] text-sm">{testimonial?.author}</p>
                  <p className="text-xs text-gray-500">{testimonial?.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Reschedule Modal (placeholder) */}
      {showRescheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold text-[#002B45] mb-4">Reschedule Consultation</h3>
            <p className="text-gray-600 mb-6">
              To reschedule your consultation, please contact our support team or visit the contact page.
            </p>
            <div className="flex space-x-4">
              <Button
                variant="outline"
                onClick={() => setShowRescheduleModal(false)}
                className="flex-1"
              >
                Close
              </Button>
              <Button
                onClick={() => window.location.href = '/contact-multi-channel-engagement-hub'}
                className="flex-1"
              >
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default BookingSummary;